import { useAction, useAtom } from '@reatom/npm-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { reatomPairResource, type PairAddress } from '@root/entities/pair'
import {
  PairExploreItem,
  PairExploreItemRoot,
  TradingPairExploreItemSkeleton,
} from '@root/features/explore/ui/pair-explore-item'
import { mergeRefs } from '@root/shared/react'
import { InfoMessage } from '@root/shared/ui-kit'
import {
  TooltipCard,
  TooltipCardParentGuardProvider,
  useTooltipCardParentGuardContext,
  type TooltipCardProps,
} from '@root/shared/ui-kit/tooltip-card'

import { useForwardWheelFromPopoverWhileOpen } from './hooks/use-forward-vertical-wheel'

import type { Network } from '@root/entities/network'

type PairPopoverByAddressProps = {
  children: React.ReactElement<React.RefAttributes<HTMLElement>>
  pairAddress: PairAddress
  network: Network.Network
  side?: TooltipCardProps['side']
}

export const PairPopoverByAddress = ({
  pairAddress,
  network,
  children,
  side = 'left',
}: PairPopoverByAddressProps) => {
  const [open, setOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const nestedOverlayOpenRef = useRef(false)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomPairResource({ pairAddress, network, loadOnConnect: false }),
    [network, pairAddress],
  )
  const [pairState] = useAtom(model)
  const loadPair = useAction(model.run)
  const refreshPair = useAction(model.refresh)

  useEffect(() => {
    if (!open) {
      return
    }
    if (
      AsyncStateN.isLoading(pairState) ||
      AsyncStateN.isSuccess(pairState) ||
      AsyncStateN.isError(pairState)
    ) {
      return
    }
    loadPair()
  }, [loadPair, open, pairState])

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next)
  }, [])

  useForwardWheelFromPopoverWhileOpen({
    open,
    triggerRef,
    contentRef,
    nestedOverlayOpenRef,
  })

  const trigger = React.cloneElement(children, {
    ref: mergeRefs([triggerRef, children.props.ref]),
  })

  const parentGuard = useTooltipCardParentGuardContext()
  const skipNested = parentGuard.cascadePreviewDepth > 1

  if (skipNested) {
    return trigger
  }

  return (
    <TooltipCardParentGuardProvider ignoreParentCloseAreaSuppression>
      <Root
        bare
        collisionPadding={8}
        contentRef={contentRef}
        nestedOverlayOpenRef={nestedOverlayOpenRef}
        open={open}
        side={side}
        sideOffset={2}
        onOpenChange={handleOpenChange}
        clickArea={AsyncStateN.matchWaiting(pairState, {
          Waiting: () => (
            <PopoverContent>
              <TradingPairExploreItemSkeleton />
            </PopoverContent>
          ),
          Error: () => (
            <PopoverContent>
              <PairExploreItemError>
                <InfoMessage intent="error" onRetry={refreshPair} />
              </PairExploreItemError>
            </PopoverContent>
          ),
          Success: (pair) => (
            <PopoverContent>
              <PairExploreItem
                pair={pair}
                statsPeriod="1D"
                priceChartInterval="minutes"
              />
            </PopoverContent>
          ),
        })}
      >
        {trigger}
      </Root>
    </TooltipCardParentGuardProvider>
  )
}

const PopoverContent = styled.div`
  display: grid;
  min-width: 0;
`

const PairExploreItemError = styled(PairExploreItemRoot)`
  cursor: default;
  place-content: center;
  padding: 16px;

  &:hover {
    background: var(--background-2);
  }
`

const Root = styled(TooltipCard)`
  --tooltip-padding: 0;
  --tooltip-background: transparent;

  display: grid;
  min-width: 0;

  /*
   * Grow to the large layout when there is room, but never exceed the space
   * the popover has on screen. PairExploreItem reads its own width and picks
   * the large/medium/small layout from there.
   */
  width: 700px;
  max-width: var(--radix-hover-card-content-available-width);

  padding: 0;
  background: transparent;
  border: none;
`
